import React, { useState } from 'react';
import { SkinProps } from '../../../../types/component-skin';

/**
 * 검색창 모던 스킨 - 더 세련된 디자인
 */
const ModernSearchBarSkin: React.FC<SkinProps> = ({ 
  data, 
  actions, 
  options = {}, 
  utils 
}) => {
  const t = utils?.t || ((text: string) => text);
  const [isFocused, setIsFocused] = useState(false);
  
  // 데이터 추출
  const { 
    searchQuery,
    placeholder,
    buttonText,
    isLoading
  } = data;
  
  // 액션 추출
  const {
    handleSearch,
    handleInputChange,
    handleKeyPress
  } = actions;
  
  // 옵션 추출
  const {
    showIcon = true,
    inputHeight = '50px',
    primaryColor = '#007bff',
    buttonColor = '#007bff',
    buttonTextColor = '#ffffff',
    borderRadius = '25px',
    fullWidth = true,
    showShadow = true,
    className = ''
  } = options;
  
  // 스타일 정의
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: fullWidth ? '100%' : 'auto',
    maxWidth: fullWidth ? '100%' : '700px',
    margin: '0 auto'
  };
  
  const wrapperStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    background: isFocused 
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
      : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    borderRadius: borderRadius,
    padding: '2px',
    border: isFocused ? '2px solid transparent' : '2px solid #e9ecef',
    boxShadow: showShadow 
      ? (isFocused 
        ? '0 12px 35px -5px rgba(102, 126, 234, 0.35)' 
        : '0 2px 8px rgba(0, 0, 0, 0.08)') 
      : 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: isFocused ? 'translateY(-2px)' : 'translateY(0)'
  };
  
  const innerWrapperStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: `calc(${borderRadius} - 4px)`,
    width: '100%',
    overflow: 'hidden',
    position: 'relative'
  };
  
  const inputStyle: React.CSSProperties = {
    flex: 1,
    height: inputHeight,
    padding: showIcon ? '0 20px 0 50px' : '0 20px',
    fontSize: '16px',
    fontWeight: '500',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    color: '#212529',
    letterSpacing: '0.3px'
  };
  
  const searchIconStyle: React.CSSProperties = {
    position: 'absolute',
    left: '20px',
    color: isFocused ? primaryColor : '#adb5bd',
    fontSize: '20px',
    transition: 'all 0.3s ease',
    transform: isFocused ? 'rotate(-10deg) scale(1.1)' : 'rotate(0) scale(1)',
    zIndex: 1
  };
  
  const buttonStyle: React.CSSProperties = {
    height: `calc(${inputHeight} - 8px)`,
    margin: '4px',
    padding: '0 30px',
    fontSize: '15px',
    fontWeight: '600',
    color: buttonTextColor || '#fff',
    background: isFocused 
      ? `linear-gradient(135deg, ${buttonColor || primaryColor} 0%, #5a67d8 100%)`
      : buttonColor || primaryColor,
    border: 'none',
    borderRadius: `calc(${borderRadius} - 8px)`,
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '110px',
    position: 'relative',
    overflow: 'hidden',
    letterSpacing: '0.5px',
    textTransform: 'none',
    boxShadow: isFocused ? '0 4px 15px rgba(0, 123, 255, 0.3)' : 'none'
  };
  
  const clearButtonStyle: React.CSSProperties = {
    position: 'absolute',
    right: '130px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: '#f8f9fa',
    border: 'none',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: searchQuery ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6c757d',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    opacity: searchQuery ? 1 : 0,
    pointerEvents: searchQuery ? 'auto' : 'none'
  };
  
  return (
    <div className={`search-bar-modern ${className}`} style={containerStyle}>
      <div style={wrapperStyle}>
        <div style={innerWrapperStyle}>
          {showIcon && (
            <i className="fas fa-search" style={searchIconStyle}></i>
          )}
          <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          style={inputStyle}
          disabled={isLoading}
        />
        {searchQuery && (
          <button
            type="button"
            style={clearButtonStyle}
            onClick={() => handleInputChange('')}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e9ecef';
              e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f8f9fa';
              e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        )}
        <button
          onClick={handleSearch}
          style={buttonStyle}
          onMouseEnter={(e) => {
            if (!isLoading && searchQuery.trim()) {
              e.currentTarget.style.transform = 'translateX(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 123, 255, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateX(0)';
            e.currentTarget.style.boxShadow = isFocused ? '0 4px 15px rgba(0, 123, 255, 0.3)' : 'none';
          }}
          disabled={isLoading || !searchQuery.trim()}
        >
          {isLoading ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          ) : (
            <>
              <span style={{ position: 'relative', zIndex: 1 }}>{buttonText}</span>
              <i className="fas fa-arrow-right" style={{ 
                marginLeft: '8px', 
                fontSize: '14px',
                transition: 'transform 0.3s ease',
                transform: 'translateX(0)'
              }}></i>
            </>
          )}
        </button>
        </div>
      </div>
      
      {/* 인기 검색어 또는 최근 검색어 표시 영역 (옵션) */}
      {options.showSuggestions && searchQuery && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: '8px',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          padding: '12px',
          zIndex: 1000
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            {t('추천 검색어')}
          </div>
          {/* 추천 검색어 리스트는 필요시 구현 */}
        </div>
      )}
    </div>
  );
};

export default ModernSearchBarSkin;